
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { trackApplyClick } from '../lib/analytics';
import { supabase } from '../lib/supabaseClient';
import Logo from './Logo';

interface HeaderProps {
  currentView?: string;
}

const Header: React.FC<HeaderProps> = ({ currentView }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [applicationsOpen, setApplicationsOpen] = useState<boolean | null>(null);

  useEffect(() => {
    supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'applications_open')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value !== undefined) setApplicationsOpen(data.value === 'true');
      });
  }, []);

  const handleApplyClick = () => {
    trackApplyClick();
    navigate('/apply');
  };

  // Determine current view from location if not provided
  const getCurrentView = () => {
    if (currentView) return currentView;
    if (location.pathname === '/login') return 'login';
    if (location.pathname === '/apply') return 'apply';
    if (location.pathname === '/reset-password') return 'reset-password';
    return 'landing';
  };

  const view = getCurrentView();

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <Logo size="md" showText={true} className="hidden sm:flex" />
            <Logo size="md" showText={false} className="flex sm:hidden" />
          </Link>

          {/* Navigation - Centered Desktop Links */}
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            {view === 'landing' && (
              <>
                <a href="#how-it-works" className="hover:text-purple-600 transition-colors">How it works</a>
                <a href="#faq" className="hover:text-purple-600 transition-colors">FAQ</a>
              </>
            )}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3 sm:gap-4">
            {applicationsOpen && (
              <button
                onClick={handleApplyClick}
                className="bg-[#341368] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-semibold hover:bg-purple-800 transition-all active:scale-95 shadow-lg shadow-purple-900/10"
              >
                Apply to Be a Creator
              </button>
            )}

            <Link
              to="/login"
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-semibold border border-gray-200 transition-all active:scale-95 hover:bg-gray-50 ${view === 'login'
                ? 'bg-purple-50 text-purple-700 border-purple-200 shadow-inner'
                : 'text-gray-700'
                }`}
            >
              Login
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
